import { Auth } from 'aws-amplify'
import { type IUserSignupParams } from '../types/auth'
import { type IUser } from '../types/user'

export class UserNotConfirmedError extends Error {
  constructor () {
    super('User not confirmed')
    this.name = 'UserNotConfirmedError'
  }
}

export class NewPasswordRequiredError extends Error {
  cognitoUser: any
  constructor (cognitoUser: any) {
    super('New password required')
    this.name = 'NewPasswordRequiredError'
    this.cognitoUser = cognitoUser
  }
}

export interface CompleteNewPasswordInput {
  cognitoUser: any
  newPassword: string
}

export interface ResetPasswordInput {
  email: string
  code: string
  newPassword: string
}

export default class AuthService {
  async signup (params: IUserSignupParams): Promise<void> {
    const { email, password } = params
    await Auth.signUp({
      username: email,
      password,
      attributes: {
        email
      },
      autoSignIn: {
        enabled: true
      }
    })
  }

  async login (email: string, password: string): Promise<IUser> {
    try {
      const user = await Auth.signIn(email, password)
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        throw new NewPasswordRequiredError(user)
      }
      return {
        id: user.attributes.sub
      }
    } catch (e) {
      console.log(e)
      if (e instanceof Error && e.name === 'UserNotConfirmedException') {
        throw new UserNotConfirmedError()
      }
      if (e instanceof Error && e.name === 'PasswordResetRequiredException') {
        console.log('here')
        throw new Error('Password reset required')
      }
      throw e
    }
  }

  async completeNewPassword (input: CompleteNewPasswordInput): Promise<void> {
    await Auth.completeNewPassword(input.cognitoUser, input.newPassword)
  }

  async signout (): Promise<void> {
    await Auth.signOut()
  }

  async resendConfirmationCode (email: string): Promise<void> {
    await Auth.resendSignUp(email)
  }

  async confirmSignUp (email: string, code: string): Promise<void> {
    await Auth.confirmSignUp(email, code)
  }

  async getSavedAuthenticatedUser (): Promise<IUser | undefined> {
    try {
      const user = await Auth.currentAuthenticatedUser()
      return {
        id: user.attributes.sub
      }
    } catch (e) {
      return undefined
    }
  }

  async sendForgetPasswordCode (email: string): Promise<void> {
    await Auth.forgotPassword(email)
  }

  async resetPassword (input: ResetPasswordInput): Promise<void> {
    await Auth.forgotPasswordSubmit(input.email, input.code, input.newPassword)
  }
}
